// © Microsoft Corporation. All rights reserved.

import { ActivityData, User } from "@msnews/social-data-service";
import { ComponentConnector, FollowInfo, UsersConnector } from "../../Social.props";
import { ProfileActivityItemTelemetryObjects, profileActivityItemTelemetryContracts } from "./ProfileActivity.telemetry";

import FollowButton from "../../follow-button/FollowButton";
import ProfileActivityReadMarker from "./ProfileActivityReadMarker";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { SocialState } from "../../Social.state";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import UserCard from "../../comment/user-card/UserCard";
import { addOrUpdateTelemetryObject } from "../../Social.telemetry";
import avatarFallback from "../../utils/avatarFallback";
import { chevron_down } from "../../utils/icons";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import { enums } from "../../utils/enums";
import getAgoText from "../../utils/getAgoText";
import { hasThemeBase } from "../../utils/themeHelpers";
import openProfilePage from "../../utils/openProfilePage";
import { placeholderImages } from "../../utils/images";

export interface ProfileFollowActivityProps {
    componentConnector: ComponentConnector;
    t: SocialLocalizedStrings;
    usersConnector: UsersConnector;
    activity: ActivityData;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    managedClasses: SocialClassNameContract;
    market: string;
    me: User;
    mainTelemetryObject: TelemetryObjectBase;
    theme?: string;
    responsiveClass: string;
}

interface ProfileFollowActivityState {
    userCardVisible: boolean;
    isHoverUserCard: boolean;
}

const mapStateToProps = function(state: SocialState) {
    return {
        me: state.me,
        users: state.users,
        theme: state.theme
    };
};

class ProfileFollowActivity extends React.Component<ProfileFollowActivityProps, ProfileFollowActivityState> {
    activityItemRef: React.RefObject<HTMLLIElement>;
    telemetryObjects: ProfileActivityItemTelemetryObjects;

    constructor(props) {
        super(props);
        this.state =  {
            userCardVisible: false,
            isHoverUserCard: false
        };

        this.activityItemRef = React.createRef();
        this.telemetryObjects = null;
    }

    showUserCard(e, isHoverUserCard= true) {
        this.setState({userCardVisible: true, isHoverUserCard: isHoverUserCard});
    }

    render() {
        const { responsiveClass, componentConnector, activity, t, managedClasses, users, market, usersConnector, me, mainTelemetryObject, theme } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            activityItem: addOrUpdateTelemetryObject(mainTelemetryObject, profileActivityItemTelemetryContracts.activityItem, {id: activity.id}, this.telemetryObjects && this.telemetryObjects.activityItem)
        };

        return (
            <li
                key={activity.id.toString()}
                ref={this.activityItemRef}
                className={classNames(
                    managedClasses.profileActivityContainer,
                    managedClasses.profileActivityFollowContainer,
                    !activity.isSeen ? "new" : "",
                    [managedClasses.profileActivitySuperAppContainer, isSuperApp],
                    [managedClasses.profileActivityFollowSuperAppContainer, isSuperApp],
                    responsiveClass
                )}
            >
                {this.activityItemRef.current && <ProfileActivityReadMarker {...{activity, usersConnector, activityItemRef: this.activityItemRef.current}} />}
                <div className="cwt-start">
                    <a {...openProfilePage(componentConnector, theme, activity.user.id, market)}>
                        <img
                            className="cwt-avatar"
                            alt={t.AriaLabelsProfilePicture}
                            src={activity.user.avatarUrl || placeholderImages.avatar}
                            onError={(e)=>{ avatarFallback(e); }}
                            onMouseEnter={(e) => this.showUserCard(e)}
                        />
                    </a>
                </div>
                <div className="cwt-mid">
                    <div className="cwt-text">
                        <a
                            className="cwt-link cwt-name"
                            onMouseEnter={(e) => this.showUserCard(e)}
                            {...openProfilePage(componentConnector, theme, activity.user.id, market)}
                        >{activity.user.name}</a> {t.ProfileActivityFollowMsg}
                        { !isSuperApp &&
                            <i className="cwt-link cwt-visible-on-focus" role="button" aria-label={t.AriaLabelsShowUserCard} onClick={(e) => this.showUserCard(e, false)}> {chevron_down} </i>
                        }
                        {this.state.userCardVisible &&
                            <UserCard
                                {...{t, managedClasses, usersConnector, users, market, me, id: activity.user.id, visible: true,
                                    onHide: () => this.setState({userCardVisible: false, isHoverUserCard: false}),
                                    triggerRef: this.activityItemRef,
                                    isHover: this.state.isHoverUserCard,
                                    mainTelemetryObject
                                }}
                            />
                        }
                    </div>
                    <div className="cwt-sub-text">
                        {getAgoText(activity.datetime)}
                    </div>
                </div>
                <div className="cwt-end">
                    <FollowButton {...{userId: activity.user.id, managedClasses, usersConnector, t}} />
                </div>
            </li>
        );
    }
}

export default connect(mapStateToProps)(ProfileFollowActivity);